<template>
  <div
    ref="rootRef"
    class="sp-radio"
    :class="classModifiers"
    part="base"
    role="radio"
    :aria-checked="ariaChecked"
    :aria-disabled="ariaDisabled"
    :tabindex="tabindex"
  >
    <span class="sp-radio__control" part="control" aria-hidden="true">
      <span class="sp-radio__dot"></span>
    </span>
    <span class="sp-radio__label" part="label">
      <slot>{{ label }}</slot>
    </span>
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";

import { useExpose } from "#ce/composables/useExpose";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

const props = defineProps({
  value: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: undefined,
  },
  // Driven by the parent sp-radio-group via setAttribute — do not set directly.
  /** @type {Boolean|String} */
  checked: booleanAttribute(),
  /** @type {Boolean|String} */
  disabled: booleanAttribute(),
  // Roving tabindex flag driven by the parent sp-radio-group via setAttribute.
  /** @type {Boolean|String} */
  tabbable: booleanAttribute(),
});

const rootRef = useTemplateRef("rootRef");

const isChecked = computed(() => toBoolean(props.checked));
const isDisabled = computed(() => toBoolean(props.disabled));
const isTabbable = computed(() => toBoolean(props.tabbable));

const ariaChecked = computed(() => String(isChecked.value));
const ariaDisabled = computed(() => (isDisabled.value ? "true" : undefined));

// Only one radio per group is in the tab order at a time (roving tabindex).
// Disabled radios are never focusable.
const tabindex = computed(() => (isTabbable.value && !isDisabled.value ? 0 : -1));

const classModifiers = computed(() => ({
  "--checked": isChecked.value,
  "--disabled": isDisabled.value,
}));

const { exposeMethods } = useExpose();

exposeMethods({
  focusRadio() {
    rootRef.value?.focus();
  },
});
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-radio {
  --control-size: var(--sp-comp-radio-control-size, var(--sp-ref-spacing-9, 1.125rem));
  --control-border-width: var(--sp-comp-radio-control-border-width, var(--sp-ref-spacing-1, 0.125rem));
  --control-border-color: var(--sp-comp-radio-control-border-color, #aaa);
  --control-border-color-checked: var(
    --sp-comp-radio-control-border-color-checked,
    var(--sp-sys-color-primary, #225ea6)
  );
  --dot-size: var(--sp-comp-radio-dot-size, var(--sp-ref-spacing-5, 0.625rem));
  --dot-color: var(--sp-comp-radio-dot-color, var(--sp-sys-color-primary, #225ea6));

  display: flex;
  align-items: center;
  gap: var(--sp-comp-radio-gap, var(--sp-ref-spacing-4, 0.5rem));
  cursor: pointer;
  outline: none;

  &:focus-visible .sp-radio__control {
    outline: var(--sp-comp-radio-focus-outline-width, var(--sp-ref-spacing-1, 0.125rem)) solid
      var(--sp-comp-radio-focus-outline-color, var(--sp-sys-color-primary, #225ea6));
    outline-offset: var(--sp-comp-radio-focus-outline-offset, var(--sp-ref-spacing-1, 0.125rem));
  }

  &.--checked {
    .sp-radio__control {
      border-color: var(--control-border-color-checked);
    }

    .sp-radio__dot {
      transform: scale(1);
    }
  }

  &.--disabled {
    cursor: not-allowed;
    opacity: var(--sp-comp-radio-disabled-opacity, 0.4);
  }
}

.sp-radio__control {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  box-sizing: border-box;
  width: var(--control-size);
  height: var(--control-size);
  border: var(--control-border-width) solid var(--control-border-color);
  border-radius: 50%;
  transition: border-color 0.15s ease-in-out;
}

.sp-radio__dot {
  width: var(--dot-size);
  height: var(--dot-size);
  border-radius: 50%;
  background-color: var(--dot-color);
  transform: scale(0);
  transition: transform 0.15s ease-in-out;
}

.sp-radio__label {
  color: var(--sp-comp-radio-label-color, currentColor);
  font: var(--sp-comp-radio-label-font, inherit);
  // Inherit the body font, but give the control label its own (tighter)
  // line-height instead of the body's loose one — otherwise each radio row is
  // padded to the body line-height (e.g. 32px) and stacked radios look far
  // apart. Overridable per consumer via the comp token.
  line-height: var(--sp-comp-radio-label-line-height, var(--sp-ref-line-height-small));
  user-select: none;
}
</style>
